<script lang="ts" context="module">
    type A = unknown
    type E = unknown
    type T = Effect.Effect<A, E, never>
</script>

<script lang="ts" generics="A, E, T extends Effect.Effect<A, E, never>">
    import { resultify } from "@utils/utilities/effect/result"

    import { friendlyError } from "@utils/utilities/friendly-error"

    import { Toasts } from "@utils/utilities/toasts"

    import type { IconType } from "@components/material/Icon.module"
    import { twMerge } from "tailwind-merge"
    import ButtonIcon from "./ButtonIcon.svelte"
    import { Effect } from "effect"
    /**
     * If `go` is a promise, it will create a loading state while the promise finishes
     */
    export let go: string | (() => unknown | Promise<unknown>) = () => {}
    /**
     * if effect is false, it means nothing will happen. easier to handle disable stuff
     */
    export let effect: T | undefined | false | null = undefined
    /**
     * Defaults to toasting
     * @param err
     */
    export let onerror: (error: E) => void = (err) => Toasts.error(friendlyError(err))
    export let onsuccess: (result: A) => void = () => {}

    $: actualFn =
        effect !== undefined
            ? effect === false || effect === null
                ? Effect.void
                : () =>
                      resultify(effect).then((res) => {
                          if (res.isOk) return onsuccess(res.value)
                          console.error(res.error)
                          return onerror(res.error)
                      })
            : go

    export let loading = false

    export let icon: IconType | null = null

    export let pinging = false
    export let xs = false
    export let small = false
    export let large = false
    $: sizeStyles = xs
        ? "app-button-xs"
        : small
          ? "app-button-small"
          : large
            ? "app-button-large"
            : ""

    export let fullwidth = false
    $: fullwidthStyles = fullwidth ? "w-full" : ""

    /**
     * go is better than on:click because it takes the disabled state into account
     * @param e
     */
    function processLink(e: Event) {
        // Return if we're a link
        if (typeof actualFn !== "function") return

        // Run actualFn if we're not disabled
        if (!disabled) {
            const rtn = actualFn()
            if (rtn instanceof Promise) {
                loading = true
                rtn.finally(() => (loading = false))
            }
        }
    }
    export let disabled = false

    export let color: "blue" | "purple" | "purple-soft" | "white" | "gold" | "danger" = "blue"
    export let naked = false
    export let outline = false
    export let text = false
    $: buttonStyles = processColor(color, outline, naked, text)
    function processColor(c: typeof color, o: boolean, n: boolean, t: boolean) {
        // Naked inherits outline
        if (n) o = true

        return twMerge(
            `app-button-${c}`,
            o ? "app-button-outline" : "",
            n ? "app-button-naked" : "",
            t ? "app-button-text" : "",
        )
    }

    $: disabledStyles = disabled ? "app-button-disabled" : ""
    $: loadingStyles = loading ? "app-button-loading" : ""
    export let classList = ""
    export { classList as class }
</script>

<svelte:element
    this={typeof actualFn !== "string" ? "button" : "a"}
    href={typeof actualFn !== "string" ? null : actualFn}
    type={typeof actualFn !== "string" ? "button" : null}
    disabled={typeof actualFn !== "string" ? disabled : null}
    tabindex={disabled ? -1 : 0}
    class={twMerge(
        "app-button",
        buttonStyles,
        fullwidthStyles,
        disabledStyles,
        loadingStyles,
        sizeStyles,
        classList,
    )}
    role={typeof actualFn === "function" ? "button" : "link"}
    on:click
    on:click={processLink}
    {...$$restProps}
    aria-disabled={disabled}
>
    {#if pinging}
        <div class="app-button-ping"></div>
    {/if}
    {#if icon}
        <ButtonIcon {icon} />
    {/if}
    <slot />
</svelte:element>

<style>
    .app-button {
        appearance: none;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        min-height: 2.75rem;
        border: 1px solid var(--app-button-secondary-border);
        border-radius: var(--radius-button);
        background: var(--app-button-secondary-bg);
        box-shadow: none;
        color: var(--app-button-secondary-text);
        cursor: pointer;
        font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        font-size: 1rem;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 1;
        padding: 0.875rem 1.25rem;
        text-align: center;
        text-decoration: none;
        text-transform: none;
        transition:
            transform 0.16s ease,
            border-color 0.16s ease,
            background-color 0.16s ease,
            color 0.16s ease,
            opacity 0.16s ease,
            filter 0.16s ease;
    }

    .app-button:hover {
        border-color: var(--app-button-secondary-hover-border);
        background: var(--app-button-secondary-hover-bg);
        color: var(--app-button-secondary-hover-text);
    }

    .app-button:active {
        transform: translateY(1px);
    }

    .app-button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .app-button-ping {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: var(--radius-button);
        outline: 2px solid var(--color-purple-500);
    }

    .app-button-small {
        min-height: 2.35rem;
        gap: 0.4rem;
        padding: 0.65rem 0.9rem;
    }

    .app-button-xs {
        min-height: 2rem;
        gap: 0.3rem;
        padding: 0.45rem 0.65rem;
        font-size: 0.875rem;
        white-space: nowrap;
    }

    .app-button-large {
        min-height: 3rem;
        padding: 1rem 1.5rem;
    }

    .app-button-blue,
    .app-button-purple {
        border-color: var(--app-button-primary-border);
        background: var(--app-button-primary-bg);
        color: var(--app-button-primary-text);
    }

    .app-button-blue:hover,
    .app-button-purple:hover {
        border-color: var(--app-button-primary-hover-border);
        background: var(--app-button-primary-hover-bg);
        color: var(--app-button-primary-hover-text);
    }

    .app-button-purple-soft {
        border-color: var(--app-button-soft-border);
        background: var(--app-button-soft-bg);
        color: var(--app-button-soft-text);
    }

    .app-button-purple-soft:hover {
        border-color: var(--app-button-soft-hover-border);
        background: var(--app-button-soft-hover-bg);
        color: var(--app-button-soft-hover-text);
    }

    .app-button-white {
        border-color: var(--app-button-secondary-border);
        background: var(--app-button-secondary-bg);
        color: var(--app-button-secondary-text);
    }

    .app-button-white:hover {
        border-color: var(--app-button-secondary-hover-border);
        background: var(--app-button-secondary-hover-bg);
        color: var(--app-button-secondary-hover-text);
    }

    .app-button-gold {
        border-color: var(--color-gold-500);
        background: var(--color-gold-500);
        color: var(--color-grey-0);
    }

    .app-button-gold:hover {
        border-color: var(--color-gold-600);
        background: var(--color-gold-600);
    }

    .app-button-danger {
        border-color: var(--danger);
        background: var(--danger);
        color: var(--color-grey-0);
    }

    .app-button-danger:hover {
        border-color: color-mix(in srgb, var(--danger) 86%, black);
        background: color-mix(in srgb, var(--danger) 86%, black);
        color: var(--color-grey-0);
    }

    .app-button-outline {
        background: transparent;
    }

    .app-button-blue.app-button-outline,
    .app-button-purple.app-button-outline {
        color: var(--app-button-primary-bg);
    }

    .app-button-blue.app-button-outline:hover,
    .app-button-purple.app-button-outline:hover {
        border-color: var(--app-button-secondary-hover-border);
        background: var(--app-button-secondary-hover-bg);
        color: var(--app-button-secondary-hover-text);
    }

    .app-button-gold.app-button-outline {
        color: var(--color-gold-700);
    }

    .app-button-gold.app-button-outline:hover {
        border-color: var(--color-gold-300);
        background: var(--color-gold-100);
        color: var(--color-gold-700);
    }

    .app-button-danger.app-button-outline {
        color: var(--danger);
    }

    .app-button-danger.app-button-outline:hover {
        border-color: var(--danger);
        background: color-mix(in srgb, var(--danger) 10%, transparent);
        color: var(--danger);
    }

    .app-button-naked {
        min-height: auto;
        border-color: transparent;
        background: transparent;
        padding-inline: 0.25rem;
    }

    .app-button-text {
        min-height: auto;
        border-color: transparent;
        background: transparent;
        color: var(--app-button-primary-bg);
        font-size: 0.8rem;
        font-weight: 750;
        padding: 0.35rem 0.5rem;
        text-transform: uppercase;
    }

    .app-button-text:hover {
        border-color: transparent;
        background: transparent;
        color: var(--app-button-primary-hover-bg);
    }

    .app-button-disabled,
    .app-button-loading {
        cursor: not-allowed;
        filter: grayscale(0.75);
        opacity: 0.55;
        pointer-events: none;
    }

    :global(.darkmode) .app-button-gold {
        border-color: var(--color-gold-400);
        background: var(--color-gold-400);
        color: var(--color-grey-900);
    }

    :global(.darkmode) .app-button-danger {
        border-color: var(--danger);
        background: var(--danger);
        color: var(--color-grey-0);
    }

    :global(.darkmode) .app-button-danger:hover {
        border-color: color-mix(in srgb, var(--danger) 82%, white);
        background: color-mix(in srgb, var(--danger) 82%, white);
        color: var(--color-grey-0);
    }

    :global(.darkmode) .app-button-outline,
    :global(.darkmode) .app-button-naked {
        background: transparent;
    }

    :global(.darkmode) .app-button-blue.app-button-outline:hover,
    :global(.darkmode) .app-button-purple.app-button-outline:hover,
    :global(.darkmode) .app-button-white.app-button-outline:hover {
        border-color: var(--app-button-secondary-hover-border);
        background: var(--app-button-secondary-hover-bg);
        color: var(--app-button-secondary-hover-text);
    }

    :global(.darkmode) .app-button-gold.app-button-outline:hover {
        border-color: var(--color-gold-700);
        background: var(--color-grey-800);
        color: var(--color-gold-300);
    }

    :global(.darkmode) .app-button-danger.app-button-outline:hover {
        border-color: var(--danger);
        background: color-mix(in srgb, var(--danger) 16%, transparent);
        color: var(--color-grey-0);
    }
</style>
